import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { StatCard } from '../../../../components/StatCard';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';

import {
  ActionsRow,
  Container,
  Dot,
  FilterTab,
  FilterTabs,
  Header,
  MessageBody,
  MessageCard,
  MessageHeader,
  MessageMeta,
  MessageSubject,
  MessagesList,
  NoDataContent,
  OriginBadge,
  PageSubtitle,
  PageTitle,
  ReplyLink,
  StatsRow,
  StatusBadge,
  StatusButton,
} from './styles';

type Status = 'open' | 'answered' | 'closed';
type Origin = 'mobile' | 'dashboard';

interface ISupportMessage {
  id: string;
  subject: string;
  message: string;
  origin: Origin;
  status: Status;
  created_at: string;
  updated_at: string;
  user: {
    id: string;
    fullname: string;
    email: string;
  };
  gym: {
    id: string;
    name: string;
  } | null;
}

const STATUS_LABEL: Record<Status, string> = {
  open: 'Aberta',
  answered: 'Respondida',
  closed: 'Fechada',
};

const ORIGIN_LABEL: Record<Origin, string> = {
  mobile: 'App',
  dashboard: 'Painel',
};

const FILTERS: Array<{ key: Status | 'all'; label: string }> = [
  { key: 'all', label: 'Todas' },
  { key: 'open', label: 'Abertas' },
  { key: 'answered', label: 'Respondidas' },
  { key: 'closed', label: 'Fechadas' },
];

const SupportMessages: React.FC = () => {
  const { addToast } = useToast();

  const [messages, setMessages] = useState<ISupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Status | 'all'>('all');

  useEffect(() => {
    setLoading(true);
    api
      .get('support/messages')
      .then(response => setMessages(response.data))
      .catch(() =>
        addToast({
          type: 'error',
          title: 'Erro ao buscar mensagens de suporte.',
        }),
      )
      .finally(() => setLoading(false));
  }, [addToast]);

  const stats = useMemo(
    () => ({
      total: messages.length,
      open: messages.filter(m => m.status === 'open').length,
      answered: messages.filter(m => m.status === 'answered').length,
      closed: messages.filter(m => m.status === 'closed').length,
    }),
    [messages],
  );

  const filteredMessages = useMemo(
    () =>
      filter === 'all'
        ? messages
        : messages.filter(message => message.status === filter),
    [messages, filter],
  );

  const handleChangeStatus = async (id: string, status: Status) => {
    const previous = messages;

    setMessages(current =>
      current.map(message =>
        message.id === id ? { ...message, status } : message,
      ),
    );

    try {
      await api.patch(`support/messages/${id}/status`, { status });
    } catch (err) {
      setMessages(previous);
      addToast({
        type: 'error',
        title: 'Erro ao atualizar status da mensagem.',
      });
    }
  };

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>Central de Suporte</PageTitle>
            <PageSubtitle>
              Mensagens enviadas pelos usuários do app e do painel
            </PageSubtitle>
          </div>
        </Header>

        <StatsRow>
          <StatCard title="Total" data={stats.total} />
          <StatCard title="Abertas" data={stats.open} />
          <StatCard title="Respondidas" data={stats.answered} />
          <StatCard title="Fechadas" data={stats.closed} />
        </StatsRow>

        <FilterTabs>
          {FILTERS.map(item => (
            <FilterTab
              key={item.key}
              type="button"
              active={filter === item.key}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
            </FilterTab>
          ))}
        </FilterTabs>

        {filteredMessages.length ? (
          <MessagesList>
            {filteredMessages.map(message => (
              <MessageCard key={message.id}>
                <MessageHeader>
                  <div>
                    <MessageSubject>{message.subject}</MessageSubject>
                    <MessageMeta>
                      <span>{message.user.fullname}</span>
                      <Dot />
                      <span>{message.user.email}</span>
                      <Dot />
                      <span>{message.gym?.name || 'Avulso'}</span>
                      <Dot />
                      <span>
                        há{' '}
                        {formatDistanceToNow(new Date(message.created_at), {
                          locale: ptBR,
                        })}
                      </span>
                      <OriginBadge>
                        <Icon
                          name={
                            message.origin === 'mobile'
                              ? 'smartphone'
                              : 'computer'
                          }
                          size={12}
                        />
                        {ORIGIN_LABEL[message.origin]}
                      </OriginBadge>
                    </MessageMeta>
                  </div>

                  <StatusBadge status={message.status}>
                    {STATUS_LABEL[message.status]}
                  </StatusBadge>
                </MessageHeader>

                <MessageBody>{message.message}</MessageBody>

                <ActionsRow>
                  {(['open', 'answered', 'closed'] as Status[]).map(status => (
                    <StatusButton
                      key={status}
                      type="button"
                      active={message.status === status}
                      disabled={message.status === status}
                      onClick={() => handleChangeStatus(message.id, status)}
                    >
                      {STATUS_LABEL[status]}
                    </StatusButton>
                  ))}

                  <ReplyLink
                    href={`mailto:${
                      message.user.email
                    }?subject=${encodeURIComponent(`Re: ${message.subject}`)}`}
                  >
                    <Icon name="reply" size={14} color={colors.primary} />
                    Responder por e-mail
                  </ReplyLink>
                </ActionsRow>
              </MessageCard>
            ))}
          </MessagesList>
        ) : (
          <NoDataContent>
            <Icon
              name="support_agent"
              size={28}
              color={colors.onSurfaceVariant}
            />
            <span>Nenhuma mensagem por aqui ainda.</span>
          </NoDataContent>
        )}
      </Container>
    </Painel>
  );
};

export { SupportMessages };
